import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { CatalogueGame, CatalogueTemplate } from 'src/app/models/bot/catalogue';
import { BotGuild, SignupTemplatePreview } from 'src/app/models/bot/guild';
import { PermissionValue } from 'src/app/models/discord-api/permission';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { PermissionsService } from 'src/app/services/permissions.service';

export type AuthorResubmitCatalogueDialogData = {
  template: CatalogueTemplate;
};

@Component({
  selector: 'app-author-resubmit-catalogue-dialog',
  templateUrl: './author-resubmit-catalogue-dialog.component.html',
  styleUrls: ['./author-resubmit-catalogue-dialog.component.css'],
})
export class AuthorResubmitCatalogueDialogComponent implements OnInit {
  form: FormGroup;
  gameControl = new FormControl('');
  games: CatalogueGame[] = [];
  filteredGames$: Observable<CatalogueGame[]> = of([]);
  selectedGame: CatalogueGame | null = null;
  createNewGame = false;
  guilds: BotGuild[] = [];
  guildTemplates: SignupTemplatePreview[] = [];
  loadingGuildTemplates = false;
  replaceStructure = false;
  submitting = false;

  constructor(
    private fb: FormBuilder,
    private botService: BotResourceService,
    private permissionsService: PermissionsService,
    private dialogRef: MatDialogRef<AuthorResubmitCatalogueDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AuthorResubmitCatalogueDialogData
  ) {
    const template = data.template;
    this.form = this.fb.group({
      title: [template.title, [Validators.required, Validators.minLength(2), Validators.maxLength(25)]],
      description: [
        template.description,
        [Validators.required, Validators.minLength(10), Validators.maxLength(1024)],
      ],
      notes: [template.notes || '', [Validators.maxLength(1024)]],
      source_guild_id: [null as string | null],
      source_template_id: [null as string | null],
    });
    if (template.game_id != null) {
      this.selectedGame = { id: template.game_id, name: template.game };
      this.gameControl.setValue(template.game);
    } else if (template.pending_game_name) {
      this.createNewGame = true;
      this.gameControl.setValue(template.pending_game_name);
    } else if (template.game) {
      this.gameControl.setValue(template.game);
    }
  }

  private isGameObject(value: unknown): value is CatalogueGame {
    return !!value && typeof value === 'object' && 'name' in (value as object) && 'id' in (value as object);
  }

  get pendingAddLabel(): string {
    const raw = this.gameControl.value;
    if (this.isGameObject(raw)) {
      return '';
    }
    return (raw ?? '').toString().trim();
  }

  get showAddOption(): boolean {
    const label = this.pendingAddLabel;
    if (!label) {
      return false;
    }
    return !this.games.some((g) => g.name.toLowerCase() === label.toLowerCase());
  }

  ngOnInit(): void {
    this.botService.listCatalogueGames().subscribe((games) => {
      this.games = games;
      if (this.selectedGame) {
        const match = games.find((g) => g.id === this.selectedGame.id);
        if (match) {
          this.selectedGame = match;
        }
      } else {
        const name = this.pendingAddLabel;
        const match = games.find((g) => g.name.toLowerCase() === name.toLowerCase());
        if (match) {
          this.selectedGame = match;
          this.createNewGame = false;
        } else if (name) {
          this.createNewGame = true;
        }
      }
    });
    this.filteredGames$ = this.gameControl.valueChanges.pipe(
      startWith(this.gameControl.value || ''),
      switchMap((value) => {
        const query = this.isGameObject(value) ? value.name : (value ?? '').toString();
        if (!query.trim()) {
          return of(this.games);
        }
        return this.botService.listCatalogueGames(query).pipe(map((games) => games));
      })
    );
    this.botService.getGuildsList().subscribe({
      next: (guilds) => {
        this.guilds = guilds.filter((g) => this.canManageTemplates(g));
      },
    });
  }

  private canManageTemplates(guild: BotGuild): boolean {
    if (!guild.joined) {
      return false;
    }
    if (guild.owner) {
      return true;
    }
    return this.permissionsService.hasPermission(
      Number(guild.permissions_new || guild.permissions || 0),
      PermissionValue.ADMINISTRATOR
    );
  }

  displayGame(game: CatalogueGame | string | null): string {
    if (!game) {
      return '';
    }
    return typeof game === 'string' ? game : game.name;
  }

  onAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    if (value && typeof value === 'object' && 'id' in value) {
      this.selectedGame = value as CatalogueGame;
      this.createNewGame = false;
      this.gameControl.setValue((value as CatalogueGame).name);
      return;
    }
    const name = (value || '').toString().trim();
    this.selectedGame = null;
    this.createNewGame = !!name;
    this.gameControl.setValue(name);
  }

  onGameBlur(): void {
    const raw = this.gameControl.value;
    if (this.isGameObject(raw)) {
      this.selectedGame = raw;
      this.createNewGame = false;
      return;
    }
    const name = (raw ?? '').toString().trim();
    if (!name) {
      this.selectedGame = null;
      this.createNewGame = false;
      return;
    }
    const match = this.games.find((g) => g.name.toLowerCase() === name.toLowerCase());
    if (match) {
      this.selectedGame = match;
      this.createNewGame = false;
    } else {
      this.selectedGame = null;
      this.createNewGame = true;
    }
  }

  onReplaceToggle(checked: boolean): void {
    this.replaceStructure = checked;
    if (!checked) {
      this.form.patchValue({ source_guild_id: null, source_template_id: null });
      this.guildTemplates = [];
    }
  }

  onGuildSelected(guildId: string | null): void {
    this.form.patchValue({ source_template_id: null });
    this.guildTemplates = [];
    if (!guildId) {
      return;
    }
    this.loadingGuildTemplates = true;
    this.botService.getGuild(guildId).subscribe({
      next: (guild) => {
        this.guildTemplates = guild.signup_templates || [];
        this.loadingGuildTemplates = false;
      },
      error: () => {
        this.loadingGuildTemplates = false;
      },
    });
  }

  submit(): void {
    this.onGameBlur();
    if (this.form.invalid || (!this.selectedGame && !this.createNewGame)) {
      this.form.markAllAsTouched();
      this.gameControl.markAsTouched();
      return;
    }
    if (this.replaceStructure) {
      const guildId = this.form.value.source_guild_id;
      const templateId = this.form.value.source_template_id;
      if (!guildId || !templateId) {
        this.form.get('source_guild_id')?.markAsTouched();
        this.form.get('source_template_id')?.markAsTouched();
        return;
      }
    }

    const rawGame =
      this.selectedGame?.name ||
      (typeof this.gameControl.value === 'string'
        ? this.gameControl.value.trim()
        : (this.gameControl.value as CatalogueGame)?.name || '');

    this.submitting = true;
    // Keep guild snowflakes as strings — Number() loses precision above 2^53-1.
    const sourceGuildId = this.replaceStructure ? (this.form.value.source_guild_id || null) : null;
    const sourceTemplateId = this.replaceStructure ? (this.form.value.source_template_id || null) : null;
    this.botService
      .resubmitCatalogueTemplate(this.data.template.id, {
        title: this.form.value.title.trim(),
        description: this.form.value.description.trim(),
        notes: (this.form.value.notes || '').trim(),
        game_id: this.selectedGame ? this.selectedGame.id : null,
        pending_game_name: this.createNewGame ? rawGame : null,
        source_guild_id: sourceGuildId,
        source_template_id: sourceTemplateId,
      })
      .subscribe({
        next: (updated) => {
          this.submitting = false;
          this.dialogRef.close(updated);
        },
        error: () => {
          this.submitting = false;
        },
      });
  }
}
